import type React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { createDraw, getLotteries } from '@/api/Lottery';
import { Layout } from '@/components/Layout';
import { LayoutCard } from '@/components/LayoutCard';
import AccentTitle from '@/components/UI/AccentTitle';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookSelect } from '@/components/UI/HookForm/Form/Select';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import type { Lottery } from '@/types/Lottery';

interface CreateDrawFormData {
  drawName: string;
  lotteryId: string;
  totalWinners: number;
}

interface CreateDrawProps {
  lotteryId?: string;
  onSuccess?: () => void;
}

const defaultValues: CreateDrawFormData = {
  drawName: '',
  lotteryId: '',
  totalWinners: 0,
};

const CreateDrawForm: React.FC<CreateDrawProps> = ({ lotteryId, onSuccess }) => {
  const navigate = useNavigate();
  const [lotteries, setLotteries] = useState<Lottery[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchLotteries = async () => {
      try {
        const response = await getLotteries();
        const lotteryData = response?.data?.data || [];
        setLotteries(Array.isArray(lotteryData) ? lotteryData : []);
      } catch (error) {
        console.error('Failed to fetch lotteries:', error);
        setLotteries([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLotteries();
  }, []);

  const onSubmit = async (data: CreateDrawFormData) => {
    setIsSubmitting(true);
    try {
      await createDraw({
        name: data.drawName,
        lotteryId: data.lotteryId,
        totalWinners: Number(data.totalWinners),
      });
      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/app/lottery/draws');
      }
    } catch (error) {
      console.error('Failed to create draw:', error);
      alert('Failed to create draw. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onError = (errors: any) => {
    console.error('Form validation errors:', errors);
  };

  if (isLoading) {
    return (
      <Layout>
        <LayoutCard>
          <div className='flex justify-center items-center p-8'>
            <div className='text-lg text-gray-600'>Loading...</div>
          </div>
        </LayoutCard>
      </Layout>
    );
  }

  return (
    <div>
      <AccentTitle title='Create New Draw' />
      <HookForm onSubmit={onSubmit} onError={onError} className='mt-6 max-w-2xl'>
        <div className='space-y-6'>
          {/* Draw Name */}
          <HookInput name='drawName' label='Draw Name *' type='text' placeholder='Enter draw name' required customClassName='w-full' />

          {/* Lottery Selection */}
          <HookSelect
            name='lotteryId'
            label='Select Lottery *'
            required
            customClassName='w-full'
            disabled={!!lotteryId}
            defaultValue={lotteryId || ''}
          >
            <option value=''>Select a lottery ({lotteries.length} available)</option>
            {lotteries.map((lottery) => (
              <option key={lottery.id} value={lottery.id}>
                {lottery.name}
              </option>
            ))}
          </HookSelect>

          {/* Total Winners */}
          <HookInput
            name='totalWinners'
            label='Total Winners *'
            type='number'
            placeholder='Enter total number of winners'
            min='1'
            required
            customClassName='w-full'
          />

          {/* Submit Button */}
          <div className='flex gap-4 pt-4'>
            <Button type='submit' label='Create Draw' btnStyle='primary' disabled={isSubmitting} />
            <Button type='button' label='Cancel' btnStyle='secondary' onClick={onSuccess || (() => {})} />
          </div>
        </div>
      </HookForm>
    </div>
  );
};

const CreateDraw: React.FC<CreateDrawProps> = (props) => (
  <HookFormProvider defaultValues={{ ...defaultValues, lotteryId: props.lotteryId || '' }}>
    <CreateDrawForm {...props} />
  </HookFormProvider>
);

export default CreateDraw;
